import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AlarmCdoSeverityCellComponent,
  AlarmStateCellComponent,
  AlarmStateChangedCellComponent,
  CloudAccount,
  CreateFleetDefinitionDialogData,
  DevOpsAlarm,
  ErmDevOpsApiClientService,
  FleetConfiguration,
  FleetConstantClassCollection,
  FleetDefinition,
  UpdateFleetConfigurationDialogData
} from 'erm-sdk';
import * as _ from 'lodash';
import { GridApi, GridOptions, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { CreateFleetDefinitionComponent } from '../create-fleet-definition/create-fleet-definition.component';
import { UpdateFleetConfigurationComponent } from '../update-fleet-configuration/update-fleet-configuration.component';

@Component({
  standalone: false,
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss']
})
export class AccountDetailsComponent implements OnInit {

  isLoading = false;
  alarmGridOptions: GridOptions;
  alarmGridApi?: GridApi;
  fleetDefinitionGridOptions: GridOptions;
  fleetDefinitionGridApi?: GridApi;
  fleetConfigurationGridOptions: GridOptions;
  fleetConfigurationGridApi?: GridApi;

  accountId: string;
  account?: CloudAccount;
  selectedFleetDefinition?: FleetDefinition;
  selectedFleetConfiguration?: FleetConfiguration;
  createFleetDefinitionDefaults?: FleetDefinition;
  fleetConstantClassCollection?: FleetConstantClassCollection;
  appStreamImageNames?: string[];

  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, route: ActivatedRoute, private matdialog: MatDialog) {
    this.accountId = route.snapshot.params['accountId'];

    this.alarmGridOptions = {
      columnDefs: this.createAlarmsGridColumnDefs(),
      enableCellTextSelection: true,
      rowSelection: { mode: 'singleRow' },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.alarmGridApi = params.api;
      }
    };

    this.fleetDefinitionGridOptions = {
      columnDefs: this.createFleetDefinitionGridColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.selectedFleetDefinition = event.api.getSelectedNodes().map(x => x.data)[0];
        this.getFleetConfiguration();
      },
      onGridReady: (params: GridReadyEvent) => {
        this.fleetDefinitionGridApi = params.api;
      }
    };

    this.fleetConfigurationGridOptions = {
      columnDefs: this.createFleetConfigurationGridColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      overlayNoRowsTemplate: 'select Fleet Definitions to see data / No rows to show',
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.selectedFleetConfiguration = event.api.getSelectedNodes().map(x => x.data)[0];
      },
      onGridReady: (params: GridReadyEvent) => {
        this.fleetConfigurationGridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;

      // Get account
      const getAccountResponse = await this.ermDevOpsApiClient.getCloudAccount(this.accountId);
      this.account = getAccountResponse.body?.cloudAccount;

      // Get account alarms
      const getAccountAlarmsResponse = await this.ermDevOpsApiClient.getCloudAccountAlarms(this.accountId);
      const alarms = getAccountAlarmsResponse.body?.alarms || new Array<DevOpsAlarm>();

      // Initialize alarms grid
      this.alarmGridApi?.setGridOption('rowData', alarms);

      // Get fleet definitions
      const getFleetDefinitionsResponse = await this.ermDevOpsApiClient.getFleetDefinitions(this.accountId);
      const fleetDefintions = getFleetDefinitionsResponse.body?.fleetDefinitions || new Array<FleetDefinition>();

      // Initialize fleet definitions grid
      this.fleetDefinitionGridApi?.setGridOption('rowData', fleetDefintions);

      // Initialize fleet configuration grid
      this.fleetConfigurationGridApi?.setGridOption('rowData', new Array<FleetConfiguration>());

    } finally {
      this.isLoading = false;
    }
  }

  private createAlarmsGridColumnDefs(): any[] {
    return [
      {
        headerName: 'State',
        field: 'alarmState',
        cellRendererFramework: AlarmStateCellComponent,
        width: 120,
        sort: 'asc'
      },
      {
        headerName: 'State Changed',
        field: 'alarmStateUpdatedUtc',
        cellRendererFramework: AlarmStateChangedCellComponent,
        width: 150
      },
      { headerName: 'Name', field: 'alarmName', width: 950, sort: 'asc' },
      { headerName: 'Service Level', field: 'serviceLevel', width: 140 },
      {
        headerName: 'CDO Severity',
        field: 'cdoSeverity',
        cellRendererFramework: AlarmCdoSeverityCellComponent,
        width: 140
      },
      { headerName: 'Resource', field: 'resourceName', width: 250 },
      { headerName: 'Metric', field: 'metricName' },
      { headerName: 'Description', field: 'description', width: 350 }
    ];
  }

  private createFleetDefinitionGridColumnDefs(): any[] {
    return [
      { headerName: 'Account Id', field: 'accountId' },
      { headerName: 'Customer Id', field: 'customerId' },
      { headerName: 'Desired Instances', field: 'desiredInstances' },
      { headerName: 'Disconnect Timeout In Seconds', field: 'disconnectTimeoutInSeconds' },
      { headerName: 'Fleet Id', field: 'fleetId' },
      { headerName: 'Fleet Kind', field: 'fleetKind' },
      { headerName: 'Fleet Type', field: 'fleetType' },
      { headerName: 'Idle Disconnect Timeout In Seconds', field: 'idleDisconnectTimeoutInSeconds' },
      { headerName: 'Image Name', field: 'imageName' },
      { headerName: 'Instance Type', field: 'instanceType' },
      { headerName: 'Max Available Capacity Alarm Evaluation Periods', field: 'maxAvailableCapacityAlarmEvaluationPeriods' },
      { headerName: 'Max Available Capacity Alarm Threshold', field: 'maxAvailableCapacityAlarmThreshold' },
      { headerName: 'Max Capacity', field: 'maxCapacity' },
      { headerName: 'Max User Duration In Seconds', field: 'maxUserDurationInSeconds' },
      { headerName: 'Min Available Capacity Alarm Evaluation Periods', field: 'minAvailableCapacityAlarmEvaluationPeriods' },
      { headerName: 'Min Available Capacity Alarm Threshold', field: 'minAvailableCapacityAlarmThreshold' },
      { headerName: 'Min Capacity', field: 'minCapacity' },
      { headerName: 'Scale In Adjustment', field: 'scaleInAdjustment' },
      { headerName: 'Scale In Cooldown In Seconds', field: 'scaleInCooldownInSeconds' },
      { headerName: 'Scale Out Adjustment', field: 'scaleOutAdjustment' },
      { headerName: 'Scale Out Cooldown In Seconds', field: 'scaleOutCooldownInSeconds' },
      { headerName: 'stackId', field: 'stackId' },
      { headerName: 'Uses Legacy Session Context', field: 'usesLegacySessionContext' }
    ];
  }

  private createFleetConfigurationGridColumnDefs(): any[] {
    return [
      { headerName: 'Fleet Id', field: 'fleetId' },
      { headerName: 'Customer Id', field: 'customerId' },
      { headerName: 'Account Id', field: 'accountId' },
      { headerName: 'State', field: 'state' },
      { headerName: 'Access Policy', field: 'accessPolicy' },
      { headerName: 'Priority', field: 'priority' },
      { headerName: 'Min Launch Capacity', field: 'minLaunchCapacity' }
    ];
  }

  // primary key identifier for fleet definition grid
  getRowNodeIdForFleetDefinitionGrid = (params: { data: FleetDefinition }) => params.data.fleetId;
  // primary key identifier for fleet configuration grid
  getRowNodeIdForFleetConfigurationGrid = (params: { data: FleetConfiguration }) => params.data.fleetId;

  async getFleetConfiguration(): Promise<void> {
    try {
      this.isLoading = true;
      if (this.selectedFleetDefinition) {
        const accountId = this.selectedFleetDefinition.accountId as string;
        const fleetId = this.selectedFleetDefinition.fleetId as string;
        this.fleetConfigurationGridOptions['api']?.showLoadingOverlay();
        const getFleetConfigurationResponse = await this.ermDevOpsApiClient.getFleetConfigurations(accountId, fleetId);
        let fleetConfigurations = getFleetConfigurationResponse.body?.fleetConfigurations || new Array<FleetConfiguration>();
        if (fleetConfigurations.length > 0 && !fleetConfigurations[0]) {
          // reset fleet configuration array if empty element found
          fleetConfigurations = new Array<FleetConfiguration>();
        }
        this.fleetConfigurationGridApi?.setGridOption('rowData', fleetConfigurations);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async openCreateFleetDefinitionDialog(): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.createFleetDefinitionDefaults) {
        // fetch defaults && constant class collection for creating fleet definition only if its not already fetched earlier
        const getFleetDefinitionDefaultsResponse = await this.ermDevOpsApiClient.getFleetDefinitionDefaults();
        this.createFleetDefinitionDefaults = getFleetDefinitionDefaultsResponse.body?.fleetDefinitionDefaults || new FleetDefinition();
        this.createFleetDefinitionDefaults.accountId = this.accountId;
      }
      if (!this.fleetConstantClassCollection) {
        const getFleetConstantClassCollection = await this.ermDevOpsApiClient.getFleetConstantClassCollection();
        this.fleetConstantClassCollection = getFleetConstantClassCollection.body?.fleetConstantClassCollection || new FleetConstantClassCollection()
      }
      if(!this.appStreamImageNames) {
        const getAppStreamImages = await this.ermDevOpsApiClient.getAppStreamImages();
        this.appStreamImageNames = getAppStreamImages.body?.names || [];
      }
      const createFleetDefinitionDialogRef = this.matdialog.open(CreateFleetDefinitionComponent, {
        data: {
          fleetDefinitionDefaults: this.createFleetDefinitionDefaults,
          fleetConstantClassCollection: this.fleetConstantClassCollection,
          appStreamImages: this.appStreamImageNames
        } as CreateFleetDefinitionDialogData
      });

      createFleetDefinitionDialogRef.afterClosed().subscribe(async dialogResult => {
        const result = dialogResult;
        if (result && result.data) {
          // update fleet definition grid
          const newFleetDefinition = result.data as FleetDefinition;
          // add customer id as local request object does not its value
          newFleetDefinition.customerId = this.account?.customerId;
          this.fleetDefinitionGridApi?.applyTransaction({
            add: [newFleetDefinition]
          });
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  canDeleteFleetDefinition(): boolean {
    return !this.selectedFleetDefinition;
  }

  async deleteFleetDefinition(): Promise<void> {
    try {
      this.isLoading = true;

      const fleetId = this.selectedFleetDefinition?.fleetId as string;
      await this.ermDevOpsApiClient.deleteFleetDefinition(fleetId);
      // remove selected item from fleet definition grid
      this.fleetDefinitionGridApi?.applyTransaction({
        remove: [this.selectedFleetDefinition]
      });
      this.selectedFleetDefinition = undefined;
      // reset fleet configuration grid
      this.fleetConfigurationGridApi?.setGridOption('rowData', new Array<FleetConfiguration>());
    } finally {
      this.isLoading = false;
    }
  }

  async updateFleetConfiguration(caller: string): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.fleetConstantClassCollection) {
        // fetch constant class collection
        const getFleetConstantClassCollection = await this.ermDevOpsApiClient.getFleetConstantClassCollection();
        this.fleetConstantClassCollection = getFleetConstantClassCollection.body?.fleetConstantClassCollection || new FleetConstantClassCollection()
      }

      let fleetConfiguration = new FleetConfiguration();
      fleetConfiguration.accountId = this.accountId;
      // update fleet configuration for newly created fleet definition
      fleetConfiguration.fleetId = this.selectedFleetDefinition?.fleetId;
      if (caller === 'fleetConfiguration') {
        // update fleet configuration for existing selected fleet configuration
        fleetConfiguration = this.selectedFleetConfiguration as FleetConfiguration;
      }

      const updateFleetConfigurationDialogRef = this.matdialog.open(UpdateFleetConfigurationComponent, {
        data: {
          fleetConfigurationDefaults: fleetConfiguration,
          fleetConstantClassCollection: this.fleetConstantClassCollection
        } as UpdateFleetConfigurationDialogData
      });

      updateFleetConfigurationDialogRef.afterClosed().subscribe(async dialogResult => {
        const result = dialogResult;
        if (result && result.data) {
          // replace fleet configuration grid data
          this.fleetConfigurationGridApi?.setGridOption('rowData', [result.data as FleetConfiguration]);
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  canUpdateFleetConfiguration(caller: string): boolean {
    switch (caller) {
      case 'fleetDefinition':
        return !this.selectedFleetDefinition;
      case 'fleetConfiguration':
        return !this.selectedFleetConfiguration;
      default:
        return true; // disables update fleet configuration button
    }
  }

  async deletefleetConfiguration(): Promise<void> {
    try {
      this.isLoading = true;

      const fleetId = this.selectedFleetConfiguration?.fleetId as string;
      await this.ermDevOpsApiClient.deleteFleetConfiguration(fleetId);
      // remove selected item from fleet definition grid
      this.fleetConfigurationGridOptions['api']?.applyTransaction({
        remove: [this.selectedFleetConfiguration]
      });
      this.selectedFleetConfiguration = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  canDeletefleetConfiguration(): boolean {
    return !this.selectedFleetConfiguration;
  }
}
